/* =====================================================================
 * useRouteSync — URL pathname ↔ chatStore.activeMode 양방향 동기화
 *
 * WorkspaceShell 내에서 호출하여:
 *  1. URL이 바뀌면 → chatStore.activeMode / activeConversation 갱신
 *  2. 사이드바 등에서 activeMode가 바뀌면 → URL을 navigate()로 갱신
 *
 * 무한 루프를 방지하기 위해 "변경 원인 추적" ref를 사용합니다.
 * ===================================================================== */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigationStore } from '@auw/stores';
import { upsertConversationEntity } from '@auw/stores';
import { isWorkspaceItemUnavailableError } from '@auw/workspace-utils';
import type { WorkspaceMode } from '@auw/workspace-utils';
import { getRouteByPath, extractIdFromPath, modeToPath } from '@auw/workspace-utils';
import { resolveWorkspaceRoute } from '../workspace/navigation/useWorkspaceRoute';
import {
  applyRouteConversationHydration,
} from './routeHydration';
import { runGuardedRouteNavigation } from './guardedRouteNavigation';

/**
 * URL → Store 동기화 (라우트가 변경되면 store를 업데이트)
 *
 * Layout 레벨에서 한 번만 마운트합니다.
 */

/**
 * 내부 채팅 아이템(문서 채팅 · GTC 채팅)이 활성일 때 URL 이 가리켜야 할 부모 id.
 * 문서 에이전트는 `docAgentDocumentId`, GTC 에이전트는 `gtcAnalysisId` 로 부모를 적는다.
 * 그 밖의 대화면 `null`.
 */
const INTERNAL_CHAT_PARENT_KEY: Readonly<Partial<Record<WorkspaceMode, string>>> = {
  'doc-agent': 'docAgentDocumentId',
  'gtc-agent': 'gtcAnalysisId',
};

function internalChatParentId(
  conv: { workspaceSettings?: Record<string, unknown> } | null | undefined,
  mode: WorkspaceMode | null,
): string | null {
  const key = mode ? INTERNAL_CHAT_PARENT_KEY[mode] : undefined;
  const value = key ? conv?.workspaceSettings?.[key] : undefined;
  return typeof value === 'string' && value ? value : null;
}

export function useRouteSyncToStore(): void {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const setActiveMode = useChatStore((s) => s.setActiveMode);
  const setActiveConversation = useChatStore((s) => s.setActiveConversation);
  const rememberModeSelection = useWorkspaceNavigationStore((s) => s.rememberModeSelection);
  const clearRememberedModeSelection = useWorkspaceNavigationStore((s) => s.clearRememberedModeSelection);

  // React Router supplies the effective path for BrowserRouter basenames,
  // HashRouter desktop URLs, and in-memory navigation. Physical pathname is
  // '/' in the desktop host and cannot validate an async item response.
  const latestPathRef = useRef(pathname);
  latestPathRef.current = pathname;
  const prevPathRef = useRef('');
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    const route = resolveWorkspaceRoute(pathname);
    if (!route.entry || !route.mode) return;
    const routeMode = route.mode;

    if (pathname !== prevPathRef.current) {
      prevPathRef.current = pathname;
      const currentMode = useChatStore.getState().activeMode;
      if (currentMode !== routeMode) {
        setActiveMode(routeMode);
      }
    }

    if (!route.itemId) {
      const currentState = useChatStore.getState();
      const activeConversation = currentState.activeId
        ? currentState.conversations[currentState.activeId] ?? null
        : null;
      if (currentState.activeId !== null && activeConversation?.mode === routeMode) {
        setActiveConversation(null, { syncMode: false });
      }
      return;
    }

    const id = route.itemId;
    rememberModeSelection(routeMode, id);

    const currentState = useChatStore.getState();
    const currentActiveId = currentState.activeId;
    if (currentActiveId
        && internalChatParentId(currentState.conversations[currentActiveId], routeMode) === id) return;

    // orphan activeId는 fetch 전에 null로 해제한다. 그렇지 않으면 upsert 뒤의
    // setActiveConversation(id)가 same-id guard로 끝나 history 선택 effect가
    // 실행되지 않는다.
    const hydrationAction = applyRouteConversationHydration(
      id,
      currentActiveId,
      currentState.conversations,
      setActiveConversation,
    );
    if (hydrationAction === 'hydrated') {
      return;
    }
    if (hydrationAction === 'activate-existing') {
      return;
    }

    /* 대화가 로드된 아이템에 없는 경우: 개별 fetch (페이지네이션 범위 밖)
       - stale 응답이 현재 선택을 덮어쓰지 않도록 sequence 가드 적용 */
    const seq = ++fetchSeqRef.current;
    const requestedId = id;

    import('@auw/api-client').then(({ getItem, itemToConversation }) => {
      getItem(requestedId)
        .then((item) => {
          if (!item) return;
          if (seq !== fetchSeqRef.current) return;

          const latestPathId = extractIdFromPath(latestPathRef.current);
          if (latestPathId !== requestedId) return;

          const conv = itemToConversation(item);
          useChatStore.setState((s) => upsertConversationEntity(s, conv, { prepend: true }));
          if (useChatStore.getState().activeId !== requestedId) {
            setActiveConversation(requestedId);
          }
        })
        .catch((err) => {
          if (!isWorkspaceItemUnavailableError(err)) return;
          if (seq !== fetchSeqRef.current) return;

          const latestPathId = extractIdFromPath(latestPathRef.current);
          if (latestPathId !== requestedId) return;

          clearRememberedModeSelection(routeMode);
          runGuardedRouteNavigation(() => {
            navigate(modeToPath(routeMode), { replace: true });
          });
        });
    }).catch(() => {
      // dynamic import 실패는 라우팅 자체를 막지 않음
    });
  }, [clearRememberedModeSelection, navigate, pathname, rememberModeSelection, setActiveMode, setActiveConversation]);
}

/**
 * Store → URL 동기화 (사이드바에서 모드 전환 시 URL 갱신)
 *
 * activeMode가 바뀌었는데 URL이 아직 반영되지 않았으면 navigate합니다.
 */
export function useStoreSyncToRoute(): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeMode = useChatStore((s) => s.activeMode);
  const activeId = useChatStore((s) => s.activeId);
  const rememberModeSelection = useWorkspaceNavigationStore((s) => s.rememberModeSelection);

  const isInternalNav = useRef(false);
  const hasMountedRef = useRef(false);
  const prevPathRef = useRef(pathname);

  useEffect(() => {
    // URL-to-store sync may already have changed the store in this effect
    // batch (including StrictMode's replay). Never navigate using that stale
    // render's mode; the following render will observe the updated value.
    if (useChatStore.getState().activeMode !== activeMode) return;

    /* "전문 에이전트" (`/specialized/*`) 같은 모드-직교 라우트에서는
     * activeMode → URL sync 대상이 아니다. ROUTE_ENTRIES 에 등록되지
     * 않은 path 에서 activeMode 기준으로 navigate 하면 사용자가 방금
     * 클릭한 specialized 페이지가 즉시 통합 에이전트 페이지로 튕긴다.
     * specialized 페이지는 매니페스트가 자체 path 를 관리하므로 sync
     * 자체를 건너뛴다. */
    if (pathname.startsWith('/specialized/')) return;

    // 마운트 직후 첫 렌더에서는 URL→Store 동기화가 먼저 진행되도록 건너뜀.
    // Store 초기값과 URL이 다른 경우(새로고침) 잘못된 리다이렉트를 방지한다.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      const entry = getRouteByPath(pathname);
      if (entry && entry.mode !== activeMode) return;
    }

    if (isInternalNav.current) {
      isInternalNav.current = false;
      prevPathRef.current = pathname;
      return;
    }

    const entry = getRouteByPath(pathname);
    const pathnameChanged = prevPathRef.current !== pathname;
    prevPathRef.current = pathname;

    // 브라우저 뒤로가기/앞으로가기, OAuth 리다이렉트처럼 URL이 먼저 바뀐 경우에는
    // 이 effect가 이전 activeMode로 다시 navigate 하지 않도록 한 틱 양보한다.
    if (pathnameChanged && entry) return;

    if (entry && entry.mode === activeMode) return;

    const targetPath = modeToPath(activeMode);
    if (pathname !== targetPath) {
      runGuardedRouteNavigation(() => {
        isInternalNav.current = true;
        navigate(targetPath, { replace: false });
      });
    }
  }, [activeMode, pathname, navigate]);

  useEffect(() => {
    const entry = getRouteByPath(pathname);
    if (!entry?.hasIdParam) return;
    if (entry.mode !== activeMode) return;
    const routeId = extractIdFromPath(pathname) ?? null;

    if (!activeId) {
      // URL에 이미 item id가 있으면 URL→Store 동기화가 완료될 때까지 기다린다.
      // store가 아직 비어있다는 이유만으로 base path로 되돌리면 깜빡임/루프가 생긴다.
      if (routeId) return;
      if (pathname !== entry.path) {
        runGuardedRouteNavigation(() => {
          navigate(entry.path, { replace: true });
        });
      }
      return;
    }

    // activeId가 현재 모드와 일치하는 conversation인지 확인.
    // 다른 모드의 conversation id가 URL에 잘못 반영되는 것을 방지한다.
    const conv = useChatStore.getState().conversations[activeId];
    if (conv && conv.mode !== activeMode) return;
    // A document / GTC chat is an internal history item; the URL always opens its parent.
    const parentId = internalChatParentId(conv, activeMode);
    if (parentId) {
      rememberModeSelection(activeMode, parentId);
      if (routeId !== parentId) {
        runGuardedRouteNavigation(() => navigate(`${entry.path}/${parentId}`, { replace: true }));
      }
      return;
    }


    if (routeId) {
      if (routeId === activeId) {
        rememberModeSelection(activeMode, activeId);
        return;
      }

      const routeConversation = useChatStore.getState().conversations[routeId];
      if (!routeConversation || routeConversation.mode === activeMode) {
        return;
      }
    }

    rememberModeSelection(activeMode, activeId);

    const expectedPath = `${entry.path}/${activeId}`;
    if (pathname !== expectedPath) {
      runGuardedRouteNavigation(() => {
        navigate(expectedPath, { replace: true });
      });
    }
  }, [activeId, activeMode, navigate, pathname, rememberModeSelection]);
}

// Restored B2C API and type contracts.
export function WorkspaceRouteSync(): null {
  useRouteSyncToStore();
  useStoreSyncToRoute();
  return null;
}
